import { fetchAllRows } from "../utils/reporting.js";
import { resolveTransactionCurrencies } from "../utils/financeReporting.js";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";
import { showWarningToast } from "../utils/toast";
import { authService } from "./authService";
import { parseCommissionRate } from "../utils/commissionRate.js";
import {
  getRejectionReason,
  normalizeRejectionReason,
} from "../utils/accessRequestRejection.js";
import {
  assertSafePartnerSlug,
  isSafePartnerSlug,
  normalizePartnerSlug,
} from "../utils/authHandoff";
import {
  excludeAnalyticsTransactions,
  excludeAnalyticsUsers,
  getAnalyticsTestUserIds,
  getAnalyticsTransactionExclusionIds,
} from "./analyticsFilterService";

// Explicit partner columns (06-P3-12). Anonymous store visitors can read
// only PARTNER_PUBLIC_COLUMNS (20261025120020), so anon-reachable reads must
// never select("*"). Owner and admin reads list the full row.
export const PARTNER_PUBLIC_COLUMNS =
  "id, name, slug, status, branding_settings, custom_domain, created_at, updated_at";
export const PARTNER_OWNER_COLUMNS = `${PARTNER_PUBLIC_COLUMNS}, admin_id, default_commission_rate, invited_email, cf_custom_hostname_id, feature_announcements_opt_in`;

export const teamService = {
  // Check if a slug is available (not already taken)
  async checkSlugAvailability(slug, currentPartnerId = null) {
    try {
      const cleanSlug = normalizePartnerSlug(slug);
      if (!isSafePartnerSlug(cleanSlug)) {
        return {
          available: false,
          error:
            "Slug can only contain lowercase letters, numbers, and hyphens.",
        };
      }

      let query = supabase.from("partners").select("id").eq("slug", cleanSlug);

      // If editing existing partner, exclude their own slug
      if (currentPartnerId) {
        query = query.neq("id", currentPartnerId);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error("Check slug error:", error);
        return { available: false, error: "Error checking availability" };
      }

      return { available: !data, error: null };
    } catch (error) {
      console.error("Check slug availability error:", error);
      return { available: false, error: "Error checking availability" };
    }
  },

  // Create a new team (delegates to create_or_claim_partner RPC for atomicity)
  async createTeam(name, adminId, customSlug = null) {
    try {
      const { data: userProfile } = await supabase
        .from("user_profiles")
        .select("email")
        .eq("id", adminId)
        .single();

      const slug =
        customSlug ||
        name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      const cleanSlug = assertSafePartnerSlug(slug);

      const { data, error } = await supabase.rpc("create_or_claim_partner", {
        p_name: name,
        p_admin_id: adminId,
        p_slug: cleanSlug,
        p_email: userProfile?.email || null,
      });

      if (error) {
        if (error.code === "23505") {
          const slugError = new Error(
            "This URL slug is already taken. Please choose a different one.",
          );
          slugError.field = "slug";
          slugError.isSlugCollision = true;
          throw slugError;
        }
        console.error("Supabase create team error:", error);
        throw error;
      }

      // RPC returns the partner row directly (SETOF-less RETURNS public.partners).
      return Array.isArray(data) ? data[0] : data;
    } catch (error) {
      if (error?.message === "Invalid partner slug") throw error;
      if (error.isSlugCollision) throw error;
      console.error("Create team error:", error);
      throw new Error("Failed to create team");
    }
  },

  // Get team by admin ID (returns most recent if multiple exist)
  async getTeamByAdmin(adminId) {
    try {
      await authService.ensureFreshToken();

      const { data, error } = await supabase
        .from("partners")
        .select(PARTNER_OWNER_COLUMNS)
        .eq("admin_id", adminId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      // Rethrow so callers can tell "no partner" (null) from a failed
      // lookup and offer a retry instead of "Partner Account Required".
      console.error("Get team error:", error);
      throw error;
    }
  },

  // True when userId owns the partner. Anonymous visitors can't read
  // admin_id, so the store-owner check is its own signed-in query.
  async isTeamAdmin(partnerId, userId) {
    if (!partnerId || !userId) return false;
    const { data, error } = await supabase
      .from("partners")
      .select("id")
      .eq("id", partnerId)
      .eq("admin_id", userId)
      .maybeSingle();
    return !error && Boolean(data);
  },

  // Get team by slug (for workspace store)
  async getTeamBySlug(
    slug,
    { logErrors = true, throwOnError = false, signal } = {},
  ) {
    try {
      let query = supabase
        .from("partners")
        .select(PARTNER_PUBLIC_COLUMNS)
        .eq("slug", slug)
        .maybeSingle();
      if (signal) query = query.abortSignal(signal);
      const { data, error } = await query;

      if (error) throw error;
      return data;
    } catch (error) {
      if (logErrors) console.error("Get team by slug error:", error);
      if (throwOnError) throw error;
      return null;
    }
  },

  // Invite a customer to the partner store
  async inviteCustomer(teamId, email, fullName = "") {
    try {
      const session = await authService.getValidSession();
      if (!session?.access_token) {
        throw new Error("Session expired. Please sign in again.");
      }
      const normalizedEmail = email.trim().toLowerCase();
      const emailPattern = normalizedEmail.replace(/[\\%_*]/g, "\\$&");
      const { data: customers, error: checkError } = await supabase
        .from("partner_customers")
        .select("id, status, user_id, invite_token_used_at")
        .eq("partner_id", teamId)
        .ilike("email", emailPattern)
        .order("id");
      if (checkError) throw checkError;
      // Legacy emails can differ only by case. Do not restore a duplicate
      // when this trainee already has a usable membership.
      const existing =
        customers?.find((customer) => customer.status !== "deactivated") ||
        customers?.[0];

      // Restore previous enrollments for trainees who already had access.
      if (
        existing?.status === "deactivated" &&
        (existing.user_id || existing.invite_token_used_at)
      ) {
        await this.updateCustomerStatus(teamId, normalizedEmail, "active");
        return { data: existing, outcome: "reactivated" };
      }

      const { data, error } = await supabase.functions.invoke(
        "invite-trainee",
        {
          body: {
            partnerId: teamId,
            email: normalizedEmail,
            fullName,
            resend: false,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { data: data.data, outcome: data.outcome };
    } catch (error) {
      console.error("Invite customer error:", error);
      throw error;
    }
  },

  // The server rotates the private invitation and queues its email together.
  async resendCustomerInvitation(teamId, email, fullName) {
    try {
      const session = await authService.getValidSession();
      if (!session?.access_token) {
        throw new Error("Session expired. Please sign in again.");
      }
      const { data, error } = await supabase.functions.invoke(
        "invite-trainee",
        {
          body: {
            partnerId: teamId,
            email: email.trim().toLowerCase(),
            fullName,
            resend: true,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      console.error("Resend invitation error:", error);
      throw new Error("Failed to resend invitation email");
    }
  },

  // Get all customers of a partner
  async getCustomers(teamId) {
    try {
      const { data, error } = await supabase
        .from("partner_customers")
        .select(
          `
          *,
          user:user_profiles(full_name, email)
        `,
        )
        .eq("partner_id", teamId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Get customers error:", error);
      throw new Error("Failed to fetch customers");
    }
  },

  async removeCustomerFromPartner(partnerId, email) {
    if (!partnerId || !email) {
      throw new Error("Partner and trainee email are required");
    }

    try {
      await authService.ensureFreshToken();

      const { data, error } = await supabase.functions.invoke(
        "remove-partner-trainee",
        {
          body: { partnerId, email: email.trim().toLowerCase() },
        },
      );

      if (error) {
        let message = "Failed to remove trainee";
        try {
          const body = await error.context?.json?.();
          if (body?.error) message = body.error;
        } catch {
          // context may not be a Response or may already be consumed
        }
        throw new Error(message);
      }

      if (data?.error) throw new Error(data.error);
      return data;
    } catch (error) {
      console.error("Remove customer error:", error);
      throw error;
    }
  },

  // Update a customer's status (e.g., deactivate a trainee)
  async updateCustomerStatus(partnerId, email, status) {
    try {
      if (status === "deactivated") {
        const { data, error } = await supabase.rpc(
          "deactivate_partner_customer",
          { p_partner_id: partnerId, p_email: email },
        );
        if (error) throw error;
        return data;
      }
      if (status === "active") {
        const { data, error } = await supabase.rpc(
          "reactivate_partner_customer",
          { p_partner_id: partnerId, p_email: email },
        );
        if (error) throw error;
        return data;
      }

      const { data: existingCustomer, error: existingCustomerError } =
        await supabase
          .from("partner_customers")
          .select("metadata")
          .eq("partner_id", partnerId)
          .eq("email", email)
          .maybeSingle();
      if (existingCustomerError) throw existingCustomerError;

      // Get user_id from user_profiles if it exists
      const { data: userProfile, error: userProfileError } = await supabase
        .from("user_profiles")
        .select("id")
        .eq("email", email)
        .maybeSingle();
      if (userProfileError) throw userProfileError;

      const payload = {
        partner_id: partnerId,
        email: email,
        user_id: userProfile?.id || null,
        status: status,
      };

      if (status !== "deactivated" && existingCustomer?.metadata?.removed_at) {
        const metadata = { ...(existingCustomer.metadata || {}) };
        delete metadata.removed_at;
        delete metadata.removed_by;
        delete metadata.removed_reason;
        payload.metadata = metadata;
      }

      // Use upsert to create or update the record
      const { data, error } = await supabase
        .from("partner_customers")
        .upsert(payload, {
          onConflict: "partner_id,email",
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Update customer status error:", error);
      throw new Error("Failed to update customer status");
    }
  },

  // Get trainee progress for a partner's enrollments
  async getTraineeProgress(partnerId) {
    try {
      const { data, error } = await supabase
        .from("course_enrollments")
        .select(
          `
          id,
          user_id,
          course_id,
          progress_percentage,
          enrollment_status,
          enrolled_at,
          completed_at,
          user:user_profiles!user_id(id, full_name, email),
          course:courses!course_id(id, title, thumbnail_url)
        `,
        )
        .eq("partner_id", partnerId)
        .order("enrolled_at", { ascending: false });

      if (error) throw error;

      // Group by user
      const progressByUser = {};
      (data || []).forEach((enrollment) => {
        const email = enrollment.user?.email;
        if (!email) return;

        if (!progressByUser[email]) {
          progressByUser[email] = {
            userId: enrollment.user_id,
            email,
            fullName: enrollment.user?.full_name || "Unknown",
            enrollments: [],
            avgProgress: 0,
            totalCourses: 0,
            completedCourses: 0,
          };
        }

        progressByUser[email].enrollments.push({
          courseId: enrollment.course_id,
          courseTitle: enrollment.course?.title || "Unknown Course",
          progress: enrollment.progress_percentage || 0,
          status: enrollment.enrollment_status,
          enrolledAt: enrollment.enrolled_at,
          completedAt: enrollment.completed_at,
        });

        progressByUser[email].totalCourses++;
        if (enrollment.enrollment_status === "completed") {
          progressByUser[email].completedCourses++;
        }
      });

      // Calculate average progress
      Object.values(progressByUser).forEach((user) => {
        const totalProgress = user.enrollments.reduce(
          (sum, e) => sum + (e.progress || 0),
          0,
        );
        user.avgProgress =
          user.totalCourses > 0
            ? Math.round(totalProgress / user.totalCourses)
            : 0;
      });

      return progressByUser;
    } catch (error) {
      console.error("Get trainee progress error:", error);
      throw new Error("Failed to fetch trainee progress");
    }
  },

  // --- Partner Approval Workflow ---

  // Get all pending partner requests (Admin only)
  async getPendingPartners() {
    try {
      const { data, error } = await supabase
        .from("partners")
        .select(
          `
            *,
            admin:user_profiles!admin_id(full_name, email)
          `,
        )
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Get pending partners error:", error);
      throw new Error("Failed to fetch pending partners");
    }
  },

  // Approve a partner request
  async approvePartner(partnerId) {
    try {
      const { error: transitionError } = await supabase.rpc(
        "transition_partner_approval",
        { p_partner_id: partnerId, p_action: "approve" },
      );
      if (transitionError) throw transitionError;

      const { data, error } = await supabase
        .from("partners")
        .select(
          `
          *,
          admin:user_profiles!admin_id(id, email, full_name)
        `,
        )
        .eq("id", partnerId)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Approve partner error:", error);
      throw new Error("Failed to approve partner");
    }
  },

  // Reject a partner request while preserving its audit record.
  async rejectPartner(partnerId) {
    try {
      const { error } = await supabase.rpc("transition_partner_approval", {
        p_partner_id: partnerId,
        p_action: "reject",
      });
      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error("Reject partner error:", error);
      throw new Error("Failed to reject partner");
    }
  },

  // --- Admin Full Store Creation ---

  // Create a fully configured partner store from the admin side
  // Partner can skip onboarding entirely since slug + branding are pre-set
  async createPartnerStore({
    name,
    email,
    slug,
    storeName,
    primaryColor,
    currency,
    contactEmail,
    contactPhone,
    logoUrl,
  }) {
    email = email?.trim().toLowerCase();
    if (!email) throw new Error("Email is required");
    const cleanSlug = assertSafePartnerSlug(slug);

    // Pre-validate slug so the admin sees the conflict immediately rather
    // than after the edge function creates the auth user.
    const { available, error: slugError } =
      await this.checkSlugAvailability(cleanSlug);
    if (!available) throw new Error(slugError || "Slug is already taken");

    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin || "";
    const redirectTo = `${appUrl}/partner/onboarding`;

    const { data, error } = await supabase.functions.invoke("invite-partner", {
      body: {
        email,
        name,
        slug: cleanSlug,
        storeName: storeName || name,
        primaryColor: primaryColor || "#059669",
        currency: currency || "USD",
        contactPhone: contactPhone || null,
        logoUrl: logoUrl || null,
        redirectTo,
      },
    });
    if (error) {
      console.error("invite-partner function error:", error);
      throw new Error(error.message || "Failed to create partner store");
    }
    if (data?.error) throw new Error(data.error);

    return {
      id: data?.partner_id,
      admin_id: data?.user_id,
      emailSent: data?.email_queued !== false,
      emailError: data?.email_error || null,
    };
  },

  // --- Partner Invitation by Email (Admin Onboarding) ---

  // Invite partner by email. Delegates to the invite-partner edge function
  // which pre-creates the auth user with email_confirm=true, claims/creates
  // the partners row, and returns a magic link that lands the invitee
  // directly on /partner/onboarding with an active session. No signup or
  // email-verification step for the invitee.
  async invitePartnerByEmail(name, email) {
    email = email?.trim().toLowerCase();
    if (!email) throw new Error("Email is required");

    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin || "";
    const redirectTo = `${appUrl}/partner/onboarding`;

    const { data, error } = await supabase.functions.invoke("invite-partner", {
      body: { email, name, redirectTo },
    });
    if (error) {
      console.error("invite-partner function error:", error);
      throw new Error(error.message || "Failed to invite partner");
    }
    if (data?.error) {
      // Edge function returned a 4xx/5xx with a structured error message.
      throw new Error(data.error);
    }

    return {
      id: data?.partner_id,
      admin_id: data?.user_id,
      emailSent: data?.email_queued !== false,
      emailError: data?.email_error || null,
    };
  },

  // Resend a partner invitation. Delegates to the same invite-partner edge
  // function so each send generates a fresh magic link (Supabase magic links
  // expire) and the invitee flow is identical to the first invite.
  async resendPartnerInvitation(partnerName, partnerEmail) {
    partnerEmail = partnerEmail?.trim().toLowerCase();
    if (!partnerEmail) throw new Error("Email is required");

    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin || "";
    const redirectTo = `${appUrl}/partner/onboarding`;

    const { data, error } = await supabase.functions.invoke("invite-partner", {
      body: { email: partnerEmail, name: partnerName, redirectTo },
    });
    if (error) {
      console.error("invite-partner function error:", error);
      throw new Error(error.message || "Failed to resend partner invitation");
    }
    if (data?.error) throw new Error(data.error);
    if (data?.email_queued === false) {
      throw new Error(
        "The invitation link was created but the email could not be queued. Please resend.",
      );
    }
    return { emailSent: true };
  },

  // Get all invited (not yet registered) partners
  async getInvitedPartners() {
    try {
      const { data, error } = await supabase
        .from("partners")
        .select(PARTNER_OWNER_COLUMNS)
        .is("admin_id", null)
        .not("invited_email", "is", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Get invited partners error:", error);
      throw new Error("Failed to fetch invited partners");
    }
  },

  // --- Admin Functions ---

  // Get all teams (Admin only)
  async getAllTeams() {
    try {
      const { data, error } = await supabase
        .from("partners")
        .select(
          `
                    *,
                    admin:user_profiles!admin_id(full_name, email),
                    transactions:partner_transactions(count)
                `,
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Get all teams error:", error);
      throw new Error("Failed to fetch teams");
    }
  },

  // Get team for user (admin only — no auto-linking)
  async getTeamForUser(userId) {
    try {
      const adminTeam = await this.getTeamByAdmin(userId);
      if (adminTeam) {
        return { team: adminTeam, role: "admin" };
      }
      return { team: null, role: null };
    } catch (error) {
      console.error("Get team for user error:", error);
      return { team: null, role: null };
    }
  },

  // Find which partner subdomain a user belongs to (admin, instructor, or customer)
  // Returns { slug, role, partnerName } or null
  async getPartnerSubdomainForUser(userId) {
    try {
      // 1. Check if user is a partner admin
      const adminTeam = await this.getTeamByAdmin(userId);
      if (adminTeam?.slug) {
        return {
          slug: adminTeam.slug,
          role: "admin",
          partnerName: adminTeam.name,
        };
      }

      // 2. Check if user is an active instructor linked to a partner
      const { data: instructorLink } = await supabase
        .from("partner_instructors")
        .select("partner:partners(slug, name)")
        .eq("instructor_id", userId)
        .eq("status", "active")
        .limit(1)
        .maybeSingle();

      if (instructorLink?.partner?.slug) {
        return {
          slug: instructorLink.partner.slug,
          role: "instructor",
          partnerName: instructorLink.partner.name,
        };
      }

      // 3. Check if user is a customer of a partner
      const { data: customerLink } = await supabase
        .from("partner_customers")
        .select("partner:partners(slug, name)")
        .eq("user_id", userId)
        .in("status", ["active", "invited"])
        .limit(1)
        .maybeSingle();

      if (customerLink?.partner?.slug) {
        return {
          slug: customerLink.partner.slug,
          role: "customer",
          partnerName: customerLink.partner.name,
        };
      }

      // 4. Check if user has enrollments tied to a partner
      const { data: enrollment } = await supabase
        .from("course_enrollments")
        .select("partner:partners(slug, name)")
        .eq("user_id", userId)
        .not("partner_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (enrollment?.partner?.slug) {
        return {
          slug: enrollment.partner.slug,
          role: "student",
          partnerName: enrollment.partner.name,
        };
      }

      return null;
    } catch (error) {
      console.error("Get partner subdomain for user error:", error);
      return null;
    }
  },

  // Link invited partner to user (call explicitly during onboarding, not silently)
  async linkInvitedPartner(userId) {
    try {
      const { data: userProfile } = await supabase
        .from("user_profiles")
        .select("email")
        .eq("id", userId)
        .single();

      if (!userProfile?.email) return null;

      const { data: invitedPartner, error } = await supabase
        .from("partners")
        .select(PARTNER_OWNER_COLUMNS)
        .eq("invited_email", userProfile.email.toLowerCase())
        .is("admin_id", null)
        .maybeSingle();

      if (!invitedPartner || error) return null;

      // The row has no owner yet, so RLS forbids claiming it client-side.
      // create_or_claim_partner claims an invitation for the caller's own
      // verified email. Without a pre-configured slug the onboarding form
      // collects one and its submit performs the same claim.
      if (!invitedPartner.slug) return invitedPartner;

      const { data: claimed, error: claimError } = await supabase.rpc(
        "create_or_claim_partner",
        {
          p_name: invitedPartner.name,
          p_admin_id: userId,
          p_slug: invitedPartner.slug,
          p_email: userProfile.email,
        },
      );
      if (claimError || !claimed || claimed.admin_id !== userId) return null;
      return claimed;
    } catch (error) {
      console.error("Link invited partner error:", error);
      return null;
    }
  },

  // ===== BROKER / WORKSPACE FUNCTIONS =====

  // Update team slug and branding
  async updateTeamWorkspace(teamId, { name, slug, branding_settings }) {
    const session = await authService.getValidSession();
    if (!session?.access_token) {
      throw new Error("Session expired. Please sign in again.");
    }

    let nextBrandingSettings = branding_settings;

    if (branding_settings) {
      const { data: current, error: currentError } = await supabase
        .from("partners")
        .select("branding_settings")
        .eq("id", teamId)
        .maybeSingle();

      if (currentError) throw currentError;
      nextBrandingSettings = {
        ...(current?.branding_settings || {}),
        ...branding_settings,
      };
    }

    // Only write the keys that were passed, so a branding-only save (e.g. a
    // logo upload) never touches the slug or name.
    const updateData = {};
    if (slug !== undefined) updateData.slug = assertSafePartnerSlug(slug);
    if (nextBrandingSettings)
      updateData.branding_settings = nextBrandingSettings;
    if (name) updateData.name = name;

    const { data, error } = await supabase
      .from("partners")
      .update(updateData)
      .eq("id", teamId)
      .select()
      .single();

    if (error) {
      if (error.code === "23505" && updateData.slug) {
        const slugError = new Error(
          "This URL slug is already taken. Please choose a different one.",
        );
        slugError.field = "slug";
        slugError.isSlugCollision = true;
        throw slugError;
      }
      throw error;
    }
    return data;
  },

  // Get course offerings for a team
  async getCourseOfferings(teamId, options = {}) {
    try {
      const includeInactive =
        typeof options === "object" && options?.includeInactive === true;

      // 1. Get all available courses
      const { data: allCourses, error: coursesError } = await supabase
        .from("courses")
        .select(
          "id, title, price, price_inr, price_usd, price_cad, non_exemplar_price_inr, non_exemplar_price_usd, non_exemplar_price_cad, batch_price_inr, batch_price_usd, batch_price_cad, batch_non_exemplar_price_inr, batch_non_exemplar_price_usd, batch_non_exemplar_price_cad, in_person_batch_price_inr, in_person_batch_price_usd, in_person_batch_price_cad, in_person_batch_non_exemplar_price_inr, in_person_batch_non_exemplar_price_usd, in_person_batch_non_exemplar_price_cad, accredited_available, non_accredited_available, thumbnail_url, short_description, description_virtual, description_in_person, duration_hours, rating, credits, difficulty_level, course_types, course_categories, iso_frameworks, type_of_course",
        )
        .eq("status", "published");

      if (coursesError) throw coursesError;

      // 2. Get partner specific visibility. Public store visitors cannot read
      // inactive offering rows directly via RLS, so use the narrow visibility RPC.
      let partnerOfferings = [];
      const { data: visibilityRows, error: visibilityError } =
        await supabase.rpc("get_partner_course_visibility", {
          p_partner_id: teamId,
        });

      if (visibilityError) {
        console.warn(
          "Partner course visibility RPC unavailable, falling back to direct query:",
          visibilityError,
        );
        const { data: fallbackOfferings, error: offeringsError } =
          await supabase
            .from("partner_course_offerings")
            .select("id, course_id, is_active")
            .eq("partner_id", teamId);

        if (offeringsError) throw offeringsError;
        partnerOfferings = fallbackOfferings || [];
      } else {
        partnerOfferings = visibilityRows || [];
      }

      // 3. Merge — pricing comes from the course object directly (platform-resolved at display time)
      const mergedOfferings = allCourses.map((course) => {
        const offering = partnerOfferings.find(
          (o) => o.course_id === course.id,
        );

        return {
          id: offering?.id || `default-${course.id}`,
          partner_id: teamId,
          course_id: course.id,
          is_active: offering ? offering.is_active : true,
          course: course,
        };
      });

      return includeInactive
        ? mergedOfferings
        : mergedOfferings.filter((o) => o.is_active);
    } catch (error) {
      console.error("Get course offerings error:", error);
      throw error;
    }
  },

  async updateCourseOfferingVisibility(partnerId, courseId, isActive) {
    try {
      const now = new Date().toISOString();
      const { data: existingOffering, error: existingError } = await supabase
        .from("partner_course_offerings")
        .select("id")
        .eq("partner_id", partnerId)
        .eq("course_id", courseId)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existingOffering?.id) {
        const { data, error } = await supabase
          .from("partner_course_offerings")
          .update({ is_active: isActive, updated_at: now })
          .eq("id", existingOffering.id)
          .select("*")
          .single();

        if (error) throw error;
        return data;
      }

      const { data: course, error: courseError } = await supabase
        .from("courses")
        .select("price, price_usd, price_inr, price_cad")
        .eq("id", courseId)
        .single();

      if (courseError) throw courseError;

      const sellingPrice =
        course?.price ??
        course?.price_usd ??
        course?.price_inr ??
        course?.price_cad ??
        0;

      const { data, error } = await supabase
        .from("partner_course_offerings")
        .insert({
          partner_id: partnerId,
          course_id: courseId,
          selling_price: sellingPrice,
          is_active: isActive,
          created_at: now,
          updated_at: now,
        })
        .select("*")
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Update course offering visibility error:", error);
      throw new Error("Failed to update course visibility");
    }
  },

  async updateAllCourseOfferingsVisibility(partnerId, offerings, isActive) {
    try {
      const now = new Date().toISOString();
      const courseIds = [
        ...new Set(
          (offerings || []).map((item) => item.course_id).filter(Boolean),
        ),
      ];

      if (courseIds.length === 0) {
        return [];
      }

      const { data: existingOfferings, error: existingError } = await supabase
        .from("partner_course_offerings")
        .select("course_id")
        .eq("partner_id", partnerId);

      if (existingError) throw existingError;

      const existingCourseIds = new Set(
        (existingOfferings || []).map((item) => item.course_id),
      );

      const { error: updateError } = await supabase
        .from("partner_course_offerings")
        .update({ is_active: isActive, updated_at: now })
        .eq("partner_id", partnerId);

      if (updateError) throw updateError;

      const rowsToInsert = (offerings || [])
        .filter((offering) => !existingCourseIds.has(offering.course_id))
        .map((offering) => {
          const course = offering.course || {};
          const sellingPrice =
            course.price ??
            course.price_usd ??
            course.price_inr ??
            course.price_cad ??
            0;

          return {
            partner_id: partnerId,
            course_id: offering.course_id,
            selling_price: sellingPrice,
            is_active: isActive,
            created_at: now,
            updated_at: now,
          };
        });

      if (rowsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("partner_course_offerings")
          .insert(rowsToInsert);

        if (insertError) throw insertError;
      }

      return { updated: courseIds.length };
    } catch (error) {
      console.error("Update all course offerings visibility error:", error);
      throw new Error("Failed to update course visibility");
    }
  },

  // Get team transactions (Broker View)
  async getTeamTransactions(teamId, options) {
    if (options) return teamService.getAllTransactions({ ...options, teamId });
    const rows = await fetchAllRows(() =>
      supabase
        .from("partner_transactions")
        .select(
          "*, user:user_profiles!user_id(full_name, email), course:courses(title, course_type), team:partners(name, slug, branding_settings)",
        )
        .eq("partner_id", teamId),
    );
    return (await attachTransactionCurrencies(rows)).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  },

  async getPartnerDashboardOverview(teamId, timeZone = "UTC") {
    const { data, error } = await supabase.rpc(
      "get_partner_dashboard_overview",
      { p_partner_id: teamId, p_time_zone: timeZone },
    );

    if (error) throw error;
    return data;
  },

  // Get all transactions (Super Admin View)
  async getAllTransactions(filters = {}) {
    try {
      const [excludedUserIds, transactionExclusions] = await Promise.all([
        getAnalyticsTestUserIds(filters),
        getAnalyticsTransactionExclusionIds(),
      ]);
      // 1. Fetch partner transactions
      const partnerTxns = await fetchAllRows(() => {
        let partnerQuery = supabase.from("partner_transactions").select(
          `
          *,
          team:partners(name, slug, branding_settings),
          user:user_profiles!user_id(full_name, email),
          course:courses(title)
        `,
        );
        partnerQuery = excludeAnalyticsUsers(partnerQuery, excludedUserIds);
        partnerQuery = partnerQuery.order("created_at", { ascending: false });

        if (filters.teamId) {
          partnerQuery = partnerQuery.eq("partner_id", filters.teamId);
        }

        return partnerQuery;
      });

      // Mark as partner sales
      const allPartnerSales = (
        await attachTransactionCurrencies(partnerTxns || [])
      ).map((txn) => ({
        ...txn,
        sale_type: "partner",
      }));
      const excludedPartnerTransactionIds = new Set(
        transactionExclusions.partnerTransactionIds,
      );
      const excludedPaymentIds = new Set(transactionExclusions.paymentIds);
      const partnerSales = allPartnerSales.filter(
        (txn) =>
          !excludedPartnerTransactionIds.has(txn.id) &&
          !txn.linked_payment_ids.some((id) => excludedPaymentIds.has(id)),
      );

      // 2. Fetch direct payments (only if no specific team filter is applied, or if we want to show all)
      // Usually, if filtering by team, we only want those partner transactions.
      if (filters.teamId) {
        // A team's rows are all partner sales, so "direct" matches none.
        if (filters.saleType === "direct") return [];
        return partnerSales.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
      }

      // Fetch completed payments
      const directPayments = await fetchAllRows(() => {
        let directPaymentsQuery = supabase
          .from("payments")
          .select(
            `
          *,
          user:user_profiles!user_id(full_name, email),
          course:courses(title)
        `,
          )
          .in("payment_status", ["completed", "refunded"]);
        directPaymentsQuery = excludeAnalyticsUsers(
          directPaymentsQuery,
          excludedUserIds,
        );
        directPaymentsQuery = excludeAnalyticsTransactions(
          directPaymentsQuery,
          transactionExclusions.paymentIds,
        );
        directPaymentsQuery = directPaymentsQuery.order("created_at", {
          ascending: false,
        });
        return directPaymentsQuery;
      });

      // 3. Unify data
      // A payment behind a partner sale is already counted as that sale.
      // Razorpay partner rows link by order id; Stripe and batch partner rows
      // link by payment_id.
      const partnerOrderIds = new Set(
        allPartnerSales.map((t) => t.razorpay_order_id).filter(Boolean),
      );
      const partnerPaymentIds = new Set(
        allPartnerSales.map((t) => t.payment_id).filter(Boolean),
      );

      const directSales = (directPayments || [])
        .filter(
          (p) =>
            !partnerPaymentIds.has(p.id) &&
            (!p.razorpay_order_id || !partnerOrderIds.has(p.razorpay_order_id)),
        )
        .map((p) => ({
          id: p.id,
          partner_id: null,
          user_id: p.user_id,
          course_id: p.course_id,
          payment_method: p.payment_method,
          paid_at: p.paid_at,
          platform: p.platform,
          currency: p.currency || "UNKNOWN",
          currency_source: p.currency ? "payment" : "unknown",
          total_amount: parseFloat(p.amount),
          razorpay_payment_id: p.razorpay_payment_id,
          razorpay_order_id: p.razorpay_order_id,
          status: p.payment_status,
          created_at: p.created_at,
          commission_rate: 0,
          platform_commission: parseFloat(p.amount),
          partner_revenue: 0,
          updated_at: p.paid_at || p.created_at,
          user: p.user,
          course: p.course,
          team: { name: "Direct Sale", slug: "direct" },
          sale_type: "direct",
        }));

      // Combine and filter by sale type if specified
      let allTransactions = [...partnerSales, ...directSales];

      if (filters.saleType && filters.saleType !== "all") {
        allTransactions = allTransactions.filter(
          (t) => t.sale_type === filters.saleType,
        );
      }

      // Sort by date
      allTransactions.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      return allTransactions;
    } catch (error) {
      console.error("Error in getAllTransactions:", error);
      throw error;
    }
  },

  // Update partner commission rate
  async updatePartnerCommissionRate(partnerId, commissionRate) {
    const parsed = parseCommissionRate(commissionRate);
    if (parsed.error) throw new Error(parsed.error);
    const { data, error } = await supabase
      .from("partners")
      .update({ default_commission_rate: parsed.value })
      .eq("id", partnerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updatePartnerCustomDomain(partnerId, customDomain) {
    const cleaned = (customDomain || "")
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/+$/, "");
    const action = cleaned ? "add" : "remove";

    const { data, error } = await supabase.functions.invoke(
      "manage-custom-domain",
      {
        body: { partnerId, customDomain: cleaned, action },
      },
    );

    if (error) {
      // Extract the actual error message from the edge function response body
      let message = "Failed to save custom domain";
      try {
        const body = await error.context?.json?.();
        if (body?.error) message = body.error;
      } catch {
        // context may not be a Response or may already be consumed
      }
      throw new Error(message);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  },

  // Delete partner account (Admin or Partner Admin)
  async deleteTeam(
    teamId,
    { forceDespiteBalance = false, forceDespiteBatches = false } = {},
  ) {
    try {
      const { data, error } = await supabase.rpc("delete_partner_account", {
        p_partner_id: teamId,
        p_force_despite_balance: forceDespiteBalance,
        p_force_despite_batches: forceDespiteBatches,
      });
      if (error) {
        // Keep the server's code/message/hint so callers can show why the
        // deletion was refused (e.g. 23514 with a HINT).
        throw Object.assign(new Error(error.message), {
          code: error.code,
          hint: error.hint,
          details: error.details,
        });
      }
      return data;
    } catch (error) {
      console.error("Delete team error:", error);
      if (error?.code) throw error;
      throw new Error("Failed to delete partner account");
    }
  },

  // Remove an instructor from a partner's own roster. Scoped: only blocks if
  // the instructor has live batches *under this partner* — other partners'
  // assignments are none of this partner's business. Deletes the
  // partner_instructors row only; the instructor's profile, other partner
  // memberships, and authored courses stay intact.
  async removeInstructorFromPool(
    partnerInstructorId,
    { forceDespiteBatches = false } = {},
  ) {
    try {
      const { data: pi, error: piErr } = await supabase
        .from("partner_instructors")
        .select("id, partner_id, instructor_id, status")
        .eq("id", partnerInstructorId)
        .single();
      if (piErr) throw piErr;
      if (!pi) throw new Error("Instructor link not found");

      // Guard: instructor_id is null for unaccepted invites — nothing can be
      // assigned to them yet, so skip the check in that case.
      if (!forceDespiteBatches && pi.instructor_id) {
        const { data: liveBatches, error: liveBatchesErr } = await supabase
          .from("training_batches")
          .select("id, title, status")
          .eq("partner_id", pi.partner_id)
          .eq("instructor_id", pi.instructor_id)
          .in("status", ["draft", "published", "in_progress"]);

        if (liveBatchesErr) throw liveBatchesErr;

        if (liveBatches?.length > 0) {
          const err = new Error(
            `Cannot remove instructor: ${liveBatches.length} active batch(es) still assigned to them on this partner. Reassign or cancel the batches first, or pass forceDespiteBatches.`,
          );
          err.code = "INSTRUCTOR_HAS_LIVE_BATCHES";
          err.liveBatches = liveBatches;
          throw err;
        }
      }

      const { error } = await supabase
        .from("partner_instructors")
        .delete()
        .eq("id", partnerInstructorId);
      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error("Remove instructor from pool error:", error);
      if (error?.code) throw error;
      throw new Error("Failed to remove instructor from pool");
    }
  },

  // --- Access Approval Workflow (Paid Externally) ---

  // 1. Request External Access (Creates pending transaction)
  // All checks + INSERT + notify happen server-side in one round-trip via
  // the request-external-access edge function. The previous client-side
  // orchestration made ~9 sequential queries before the INSERT, so closing
  // the tab anywhere in the chain silently dropped the request.
  //
  // Uses fetch({ keepalive: true }) directly instead of supabase.functions
  // .invoke so the request still completes if the browser closes the tab
  // immediately after the click — supabase-js's invoke goes through normal
  // fetch which the browser aborts on page unload. The body is well under
  // the 64KB keepalive limit. Same trick the SCORM player uses on pagehide.
  //
  // The userId arg is kept for signature compatibility but ignored — the
  // edge function derives the user from the JWT, which is also a small
  // security improvement (client can't request on behalf of someone else).
  async requestExternalAccess(
    teamId,
    courseId,
    _userId, // eslint-disable-line no-unused-vars
    price,
    {
      notes = null,
      attachment_url = null,
      accreditation_type = "accredited",
    } = {},
  ) {
    try {
      await authService.ensureFreshToken();

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error("Not authenticated");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const url = `${supabaseUrl}/functions/v1/request-external-access`;

      const response = await fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: anonKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          partner_id: teamId,
          course_id: courseId,
          price,
          notes,
          attachment_url,
          accreditation_type,
        }),
      });

      // Try to parse JSON regardless of status — edge function returns
      // { error: "..." } for known 4xx cases.
      let payload = null;
      try {
        payload = await response.json();
      } catch {
        /* non-JSON response (rare); leave payload null */
      }

      if (!response.ok) {
        throw new Error(
          payload?.error || `Request failed (${response.status})`,
        );
      }
      return payload;
    } catch (error) {
      console.error("Request access error:", error);
      throw new Error(error.message || "Failed to submit access request");
    }
  },

  async getMyPendingRequests(userId, partnerId) {
    if (!userId || !partnerId) return [];

    await authService.ensureFreshToken();

    const { data, error } = await supabase
      .from("partner_transactions")
      .select("course_id")
      .eq("user_id", userId)
      .eq("partner_id", partnerId)
      .is("batch_id", null)
      .eq("status", "manual_pending");

    if (error) {
      console.error("Error fetching my requests:", error);
      return [];
    }

    const pendingRequests = data || [];
    const courseIds = [
      ...new Set(pendingRequests.map((item) => item.course_id).filter(Boolean)),
    ];

    if (courseIds.length === 0) {
      return pendingRequests;
    }

    const { data: enrollments, error: enrollmentError } = await supabase
      .from("course_enrollments")
      .select("course_id")
      .eq("user_id", userId)
      .in("course_id", courseIds)
      .neq("enrollment_status", "cancelled");

    if (enrollmentError) {
      console.error(
        "Error checking enrollments for pending requests:",
        enrollmentError,
      );
      return pendingRequests;
    }

    const enrolledCourseIds = new Set(
      (enrollments || []).map((enrollment) => enrollment.course_id),
    );

    return pendingRequests.filter(
      (request) => !enrolledCourseIds.has(request.course_id),
    );
  },

  // 2. Get Pending Requests (For Partner Dashboard)
  async getPendingAccessRequests(teamId) {
    try {
      const { data, error } = await supabase
        .from("partner_transactions")
        .select(
          `
                    *,
                    user:user_profiles!user_id(full_name, email, phone),
                    course:courses(title, thumbnail_url),
                    batch:training_batches(
                      id, title, slug, delivery_mode, start_date, end_date,
                      classroom_venue_name, classroom_city
                    )
                `,
        )
        .eq("partner_id", teamId)
        .eq("status", "manual_pending")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const requests = data || [];
      const userIds = [
        ...new Set(requests.map((item) => item.user_id).filter(Boolean)),
      ];
      const courseIds = [
        ...new Set(requests.map((item) => item.course_id).filter(Boolean)),
      ];

      if (userIds.length === 0 || courseIds.length === 0) {
        return requests;
      }

      const batchIds = [
        ...new Set(requests.map((item) => item.batch_id).filter(Boolean)),
      ];

      const [enrollmentCheck, completedTransactionCheck, batchEnrollmentCheck] =
        await Promise.all([
          supabase
            .from("course_enrollments")
            .select("user_id, course_id")
            .in("user_id", userIds)
            .in("course_id", courseIds)
            .neq("enrollment_status", "cancelled"),
          supabase
            .from("partner_transactions")
            .select("user_id, course_id, batch_id, created_at")
            .eq("partner_id", teamId)
            .eq("status", "completed")
            .in("user_id", userIds)
            .in("course_id", courseIds),
          batchIds.length > 0
            ? supabase
                .from("batch_enrollments")
                .select("user_id, batch_id, status")
                .in("user_id", userIds)
                .in("batch_id", batchIds)
                .in("status", ["confirmed", "completed"])
            : Promise.resolve({ data: [], error: null }),
        ]);

      if (enrollmentCheck.error) throw enrollmentCheck.error;
      if (completedTransactionCheck.error)
        throw completedTransactionCheck.error;
      if (batchEnrollmentCheck.error) throw batchEnrollmentCheck.error;

      const completedTransactions = completedTransactionCheck.data || [];

      // Course-level access resolves course requests only. A learner who
      // already owns the self-paced course still needs approval for a batch
      // seat, so batch requests are deduplicated by batch instead.
      const resolvedCourseKeys = new Set([
        ...(enrollmentCheck.data || []).map(
          (item) => `${item.user_id}:${item.course_id}`,
        ),
        ...completedTransactions
          .filter((item) => !item.batch_id)
          .map((item) => `${item.user_id}:${item.course_id}`),
      ]);

      // A batch request is settled only by what happened after it: a seat the
      // learner holds now, or a completed payment made after the request. An
      // older completed transaction (e.g. before the learner was removed and
      // asked again) must not hide the new request.
      const seatedBatchKeys = new Set(
        (batchEnrollmentCheck.data || []).map(
          (item) => `${item.user_id}:${item.batch_id}`,
        ),
      );
      const isBatchRequestResolved = (request) => {
        const key = `${request.user_id}:${request.batch_id}`;
        if (seatedBatchKeys.has(key)) return true;
        const requestedAt = Date.parse(request.created_at || "");
        return completedTransactions.some(
          (item) =>
            item.batch_id &&
            `${item.user_id}:${item.batch_id}` === key &&
            Number.isFinite(requestedAt) &&
            Date.parse(item.created_at || "") > requestedAt,
        );
      };

      return requests.filter((request) =>
        request.batch_id
          ? !isBatchRequestResolved(request)
          : !resolvedCourseKeys.has(`${request.user_id}:${request.course_id}`),
      );
    } catch (error) {
      console.error("Get pending requests error:", error);
      throw new Error("Failed to fetch pending requests");
    }
  },

  async updatePartnerTransactionAmount(
    transactionId,
    amount,
    { pendingOnly = false } = {},
  ) {
    try {
      await authService.ensureFreshToken();

      const { data, error } = await supabase.functions.invoke(
        "update-partner-transaction-amount",
        {
          body: { transactionId, amount, pendingOnly },
        },
      );

      if (error) {
        let details = "";
        try {
          const body = await error.context?.json?.();
          details = body?.error || body?.message || "";
        } catch {
          details = "";
        }
        throw new Error(details || error.message || "Failed to update amount");
      }

      return data?.transaction;
    } catch (error) {
      console.error("Update partner transaction amount error:", error);
      throw new Error(error.message || "Failed to update amount");
    }
  },

  // 3. Approve Access Request
  async approveAccessRequest(transactionId, { approvedAmount } = {}) {
    try {
      if (
        !["number", "string"].includes(typeof approvedAmount) ||
        String(approvedAmount).trim() === "" ||
        !Number.isFinite(Number(approvedAmount)) ||
        Number(approvedAmount) < 0
      ) {
        throw new Error("Enter a valid approved amount");
      }

      // Only act on manual_pending transactions
      const { data: statusCheck } = await supabase
        .from("partner_transactions")
        .select("status")
        .eq("id", transactionId)
        .single();
      if (statusCheck?.status !== "manual_pending") {
        throw new Error("This request has already been processed");
      }

      // A. Get detailed transaction info including user and partner branding
      const { data: txn, error: fetchError } = await supabase
        .from("partner_transactions")
        .select(
          `
          *,
          user:user_profiles!user_id(full_name, email),
          course:courses(title),
          batch:training_batches(id, title, slug),
          partner:partners(id, name, slug, custom_domain, branding_settings)
        `,
        )
        .eq("id", transactionId)
        .single();

      if (fetchError || !txn) throw new Error("Transaction not found");

      // Apply the approved amount before fulfilment so batch approvals record
      // the reviewed price: approve_external_batch_enrollment reads the stored
      // total_amount when it writes the payment and revenue split.
      const previousAmount = Number(txn.total_amount);
      const amountChanged =
        Number.isFinite(previousAmount) &&
        previousAmount !== Number(approvedAmount);
      await this.updatePartnerTransactionAmount(
        transactionId,
        Number(approvedAmount),
        { pendingOnly: true },
      );

      // If approval then fails, the request is still pending: put its
      // original amount back so a failed attempt leaves nothing changed.
      const restoreAmountAfterFailure = async (approvalError) => {
        if (!amountChanged) throw approvalError;
        try {
          await this.updatePartnerTransactionAmount(
            transactionId,
            previousAmount,
            { pendingOnly: true },
          );
        } catch (restoreError) {
          throw new Error(
            `${approvalError.message || "Approval failed"}. The amount could not be restored to ${previousAmount}: ${restoreError.message}`,
          );
        }
        throw approvalError;
      };

      if (txn.batch_id) {
        await authService.ensureFreshToken();
        const { data, error } = await supabase.rpc(
          "approve_external_batch_enrollment",
          { p_transaction_id: transactionId },
        );
        if (error) {
          await restoreAmountAfterFailure(
            new Error(error.message || "Approval failed"),
          );
        }
        return data;
      }

      // B. Use the assign-course Edge Function to handle enrollment + branded email
      // This is cleaner as it reuses the same branded template
      const partnerDomain =
        import.meta.env.VITE_PARTNER_DOMAIN || "procertcompliance.com";
      const storeUrl = txn.partner.custom_domain
        ? `https://${txn.partner.custom_domain}`
        : `https://${txn.partner.slug}.${partnerDomain}`;

      // Refresh token before edge function call to prevent 401s
      await authService.ensureFreshToken();

      const { data: assignData, error: assignError } =
        await supabase.functions.invoke("assign-course", {
          body: {
            email: txn.user.email,
            name: txn.user.full_name,
            courseIds: [txn.course_id],
            accreditationTypes: {
              [txn.course_id]: txn.accreditation_type || "accredited",
            },
            partnerId: txn.partner_id,
            teamName: txn.partner.name,
            logoUrl: txn.partner.branding_settings?.logo_url,
            primaryColor: txn.partner.branding_settings?.primary_color,
            storeUrl,
            transactionId: transactionId,
          },
        });

      if (assignError) {
        let details = "";
        try {
          const body = await assignError.context?.json?.();
          details = body?.error || body?.message || "";
        } catch (_) {
          details = "";
        }
        await restoreAmountAfterFailure(
          new Error(details || assignError.message || "Approval failed"),
        );
      }

      if (assignData?.email_queued === false) {
        showWarningToast(
          `Access approved for ${txn.user.email}, but the email could not be queued. Please resend the invitation.`,
        );
      }

      // assign-course owns enrollment creation, completes the transaction only
      // after the enrollment write succeeds, and queues the learner's
      // access_approved notification server-side.

      return { id: transactionId, status: "completed" };
    } catch (error) {
      console.error("Approve access error:", error);
      throw new Error(error.message || "Failed to approve request");
    }
  },

  // 4. Reject Access Request
  // `reason` is optional and learner-facing; blank sends p_reason null.
  async rejectAccessRequest(transactionId, { reason } = {}) {
    try {
      // Only act on manual_pending transactions
      const { data: statusCheck } = await supabase
        .from("partner_transactions")
        .select("status")
        .eq("id", transactionId)
        .single();
      if (statusCheck?.status !== "manual_pending") {
        throw new Error("This request has already been processed");
      }

      await authService.ensureFreshToken();
      const { data, error } = await supabase.rpc(
        "reject_partner_access_request",
        {
          p_transaction_id: transactionId,
          p_reason: normalizeRejectionReason(reason),
        },
      );

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Reject access error:", error);
      throw new Error("Failed to reject request");
    }
  },

  // The partner's reason on the learner's latest rejected course request in
  // this store, keyed by course id. Only the reason line is returned, never
  // the rest of the transaction notes.
  async getMyRejectedRequestReasons(userId, partnerId) {
    if (!userId || !partnerId) return {};

    const { data, error } = await supabase
      .from("partner_transactions")
      .select("course_id, notes, updated_at")
      .eq("user_id", userId)
      .eq("partner_id", partnerId)
      .is("batch_id", null)
      .eq("status", "manual_rejected")
      .order("updated_at", { ascending: false });
    if (error) throw error;

    const latest = {};
    for (const row of data || []) {
      if (row.course_id && !(row.course_id in latest)) {
        latest[row.course_id] = getRejectionReason(row.notes);
      }
    }
    return Object.fromEntries(
      Object.entries(latest).filter(([, reason]) => reason),
    );
  },
  // Enroll a trainee in a batch externally (Partner paid offline)
  async enrollInBatchExternally({ partnerId, batchId, userId, email, name }) {
    try {
      const { data: transaction, error: lookupError } = await supabase
        .from("partner_transactions")
        .select("id")
        .eq("partner_id", partnerId)
        .eq("batch_id", batchId)
        .eq("user_id", userId)
        .eq("status", "manual_pending")
        .maybeSingle();
      if (lookupError) throw lookupError;
      if (!transaction) {
        throw new Error(
          "The learner must submit an external payment request before approval.",
        );
      }
      const { data, error } = await supabase.rpc(
        "approve_external_batch_enrollment",
        { p_transaction_id: transaction.id },
      );
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Batch external enrollment error:", error);
      throw new Error(error.message || "Failed to enroll in batch");
    }
  },

  // Assign course directly to customer (Partner paid offline)
  async assignCourseToCustomer({
    teamId,
    teamName,
    logoUrl,
    primaryColor,
    storeUrl,
    email,
    name,
    courseIds,
  }) {
    try {
      // Refresh token before edge function call to prevent 401s
      await authService.ensureFreshToken();

      const { data, error } = await supabase.functions.invoke("assign-course", {
        body: {
          partnerId: teamId,
          teamName,
          logoUrl,
          primaryColor,
          storeUrl,
          email,
          name,
          courseIds,
        },
      });

      if (error) throw error;
      if (data?.email_queued === false) {
        showWarningToast(
          `Courses assigned to ${email}, but the email could not be queued. Please resend the invitation.`,
        );
      }
      return data;
    } catch (error) {
      console.error("Error assigning course:", error);
      throw error;
    }
  },

  // Check if partner has any completed transactions (for currency change validation)
  async hasTransactions(partnerId) {
    try {
      const { count, error } = await supabase
        .from("partner_transactions")
        .select("id", { count: "exact", head: true })
        .eq("partner_id", partnerId)
        .eq("status", "completed");

      if (error) throw error;
      return count > 0;
    } catch (error) {
      console.error("Error checking partner transactions:", error);
      throw error;
    }
  },

  // ---- Trainee invitation flow (SCRUM-110) ----

  // Look up a pending partner invitation by its token. Goes through the
  // get_invite_by_token SECURITY DEFINER RPC so anonymous clients can only
  // fetch the single row matching a token they already know — no broad
  // enumeration of pending invites.
  async getInviteByToken(inviteToken) {
    if (!inviteToken) return null;
    try {
      const { data, error } = await supabase.rpc("get_invite_by_token", {
        p_token: inviteToken,
      });
      if (error) throw error;
      return data || null;
    } catch (error) {
      console.error("Get invite by token error:", error);
      return null;
    }
  },

  // Claim the invitation for the currently authenticated user. Flips the
  // partner_customers row to status='active', links user_id, and stamps
  // the token as used so the link can't be reused. Uses count:'exact' so
  // we know whether a row was actually updated without relying on a
  // RETURNING clause (there is no broad SELECT policy to permit one).
  async claimInviteByToken(inviteToken, userId) {
    if (!inviteToken || !userId) {
      return { success: false, error: "Missing invite token or user id" };
    }
    try {
      const { data, error } = await supabase.rpc(
        "claim_partner_customer_invites",
        { p_token: inviteToken },
      );
      if (error) throw error;
      return { success: Number(data?.claimed_count || 0) > 0, data };
    } catch (error) {
      console.error("Claim invite error:", error);
      return { success: false, error: error.message };
    }
  },

  async claimAvailableInvitesForCurrentUser() {
    try {
      const { data, error } = await supabase.rpc(
        "claim_partner_customer_invites",
        { p_token: null },
      );
      if (error) throw error;
      return { success: Number(data?.claimed_count || 0) > 0, data };
    } catch (error) {
      console.error("Claim available invites error:", error);
      return { success: false, error: error.message };
    }
  },
};

export async function attachTransactionCurrencies(rows) {
  const lookups = [
    ["id", [...new Set(rows.map((row) => row.payment_id).filter(Boolean))]],
    [
      "razorpay_order_id",
      [...new Set(rows.map((row) => row.razorpay_order_id).filter(Boolean))],
    ],
  ];
  const payments = new Map();
  for (const [column, values] of lookups) {
    for (let index = 0; index < values.length; index += 100) {
      const found = await fetchAllRows(() =>
        supabase
          .from("payments")
          .select("id, razorpay_order_id, currency")
          .in(column, values.slice(index, index + 100)),
      );
      for (const payment of found) payments.set(payment.id, payment);
    }
  }
  return resolveTransactionCurrencies(rows, [...payments.values()]);
}
